import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Upload, X, Info, Sliders, CheckCircle2 } from 'lucide-react';

const StepCustomerDetails = ({
  customerDetails,
  onCustomerChange,
  onLogoUpload,
  profileLogoUrl,
  profileLoaded,
  totals,
  onBack,
  onContinue
}) => {
  // The PDF generator embeds the logo stored on the user's Business Profile.
  // We display that profile logo here, with the optional per-quote upload as an override.
  const effectiveLogo = customerDetails.companyLogoPreview || profileLogoUrl;
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
      <h2 className="text-xl md:text-2xl font-bold text-[#27292E] mb-2">
        Quote Details
      </h2>
      <p className="text-gray-600 mb-6">
        Enter your details and the customer information for this quote.
      </p>

      {/* Info Banner */}
      <div className="mb-6 p-4 bg-[#2F5E96]/10 border border-[#2F5E96]/30 rounded-xl">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-[#2F5E96] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-[#2F5E96]">
              Your company details &amp; logo are pulled from your <strong>Business Profile</strong>. They auto-populate below — edit them once on your profile and they&apos;re used on every quote.
            </p>
            <Link
              to="/settings"
              data-testid="open-business-profile-link"
              className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-[#2F5E96] hover:underline"
            >
              <Sliders className="w-3 h-3" />
              Manage Business Profile
            </Link>
          </div>
        </div>
        {profileLoaded && profileLogoUrl && (
          <div className="mt-3 ml-8 flex items-center gap-2 text-xs text-emerald-700">
            <CheckCircle2 className="w-4 h-4" />
            Logo loaded from your Business Profile — will appear on the generated PDF.
          </div>
        )}
      </div>

      <div className="space-y-8">
        {/* Your (Installer) Details Section */}
        <div>
          <h3 className="text-lg font-bold text-[#27292E] mb-4 pb-2 border-b border-gray-200">
            Your Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Your Name
              </Label>
              <Input
                type="text"
                value={customerDetails.yourName || ''}
                onChange={(e) => onCustomerChange('yourName', e.target.value)}
                placeholder="John Smith"
                className="h-12 border-2 rounded-xl"
                data-testid="your-name-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Company Name
              </Label>
              <Input
                type="text"
                value={customerDetails.companyName || ''}
                onChange={(e) => onCustomerChange('companyName', e.target.value)}
                placeholder="Smith Driveways Ltd"
                className="h-12 border-2 rounded-xl"
                data-testid="company-name-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Your Contact Number
              </Label>
              <Input
                type="tel"
                value={customerDetails.yourNumber || ''}
                onChange={(e) => onCustomerChange('yourNumber', e.target.value)}
                placeholder="07700 900000"
                className="h-12 border-2 rounded-xl"
                data-testid="your-number-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Company Logo
              </Label>
              {effectiveLogo ? (
                <div className="flex items-center gap-3">
                  <img
                    src={effectiveLogo}
                    alt="Company Logo"
                    className="h-12 w-auto object-contain border rounded-lg bg-white"
                    data-testid="effective-logo-preview"
                  />
                  {customerDetails.companyLogoPreview ? (
                    <button
                      onClick={() => {
                        onCustomerChange('companyLogo', null);
                        onCustomerChange('companyLogoPreview', '');
                      }}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      data-testid="remove-logo-btn"
                      title="Remove this quote's override"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  ) : (
                    <span className="text-xs text-emerald-700 font-medium">From Business Profile</span>
                  )}
                </div>
              ) : (
                <label className="flex items-center justify-center h-12 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-[#300122] hover:bg-[#300122]/5 transition-colors">
                  <Upload className="w-5 h-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-500">Upload logo (or set on profile)</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={onLogoUpload}
                    className="hidden"
                    data-testid="logo-upload-input"
                  />
                </label>
              )}
            </div>
          </div>
        </div>

        {/* Customer Details Section */}
        <div>
          <h3 className="text-lg font-bold text-[#27292E] mb-4 pb-2 border-b border-gray-200">
            Customer Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Customer Name <span className="text-red-500">*</span>
              </Label>
              <Input
                type="text"
                value={customerDetails.customerName || ''}
                onChange={(e) => onCustomerChange('customerName', e.target.value)}
                placeholder="Jane Doe"
                className="h-12 border-2 rounded-xl"
                data-testid="customer-name-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Customer Phone
              </Label>
              <Input
                type="tel"
                value={customerDetails.customerNumber || ''}
                onChange={(e) => onCustomerChange('customerNumber', e.target.value)}
                placeholder="07700 900001"
                className="h-12 border-2 rounded-xl"
                data-testid="customer-phone-input"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Project Address
              </Label>
              <Input
                type="text"
                value={customerDetails.customerAddress || ''}
                onChange={(e) => onCustomerChange('customerAddress', e.target.value)}
                placeholder="123 Example Street, City, Postcode"
                className="h-12 border-2 rounded-xl"
                data-testid="customer-address-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Running Total */}
      <div className="mt-8 p-4 bg-[#300122] text-white rounded-xl">
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-white/80">Surfacing:</span>
            <span>£{totals.surfacingTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
          </div>
          {totals.additionalTotal > 0 && (
            <div className="flex justify-between">
              <span className="text-white/80">Additional Costs:</span>
              <span>£{totals.additionalTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
            </div>
          )}
          {totals.edgingsTotal > 0 && (
            <div className="flex justify-between">
              <span className="text-white/80">Edgings:</span>
              <span>£{totals.edgingsTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
            </div>
          )}
          {totals.wallingFencingTotal > 0 && (
            <div className="flex justify-between">
              <span className="text-white/80">Walling & Fencing:</span>
              <span>£{totals.wallingFencingTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
            </div>
          )}
          {totals.miscTotal > 0 && (
            <div className="flex justify-between">
              <span className="text-white/80">Miscellaneous:</span>
              <span>£{totals.miscTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t border-white/20">
            <span className="font-bold">Total (excl. VAT):</span>
            <span className="text-2xl font-bold">
              £{totals.grandTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      <div className="flex gap-4 mt-8">
        <Button
          onClick={onBack}
          variant="outline"
          className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#300122] text-[#300122] hover:bg-[#300122]/5"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>
        <Button
          onClick={onContinue}
          className="flex-1 bg-[#300122] hover:bg-[#4a0636] text-white h-14 text-lg font-bold rounded-xl"
        >
          Review Quote
        </Button>
      </div>
    </div>
  );
};

export default StepCustomerDetails;
